/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

// Honest, non-invented facts (product properties, not performance claims).
const METRICS = [
    { value: '100%', label: 'de vos vendeurs informés en temps réel' },
    { value: '0', label: 'tableur Excel à maintenir' },
    { value: '24/7', label: 'de transparence pour vos vendeurs' },
];

/**
 * ConvProofs — Section 5. Trust band + testimonial structure.
 * No invented performance numbers — only factual product properties.
 */
export const ConvProofs = () => {
    return (
        <section className="relative py-20 md:py-32" data-testid="proofs-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.6 }}
                    className="text-center text-3xl sm:text-4xl font-bold tracking-tighter text-white max-w-2xl mx-auto"
                    style={DISPLAY}
                >
                    L'avantage compétitif des meilleures agences.
                </motion.h2>

                {/* Metrics band */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {METRICS.map((m, i) => (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            data-testid={`proof-metric-${i}`}
                            className="rounded-2xl border border-white/[0.06] bg-[#0C0A10] p-7 text-center"
                        >
                            <div
                                className="text-4xl font-bold bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent"
                                style={MONO}
                            >
                                {m.value}
                            </div>
                            <p className="mt-3 text-sm text-[#A19DAB]">{m.label}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Testimonial */}
                <motion.figure
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6 }}
                    data-testid="proof-testimonial"
                    className="relative mt-6 rounded-2xl border border-[#681793]/20 bg-gradient-to-br from-[#681793]/10 via-[#0C0A10] to-[#0C0A10] p-8 md:p-12 max-w-3xl mx-auto"
                >
                    <Quote className="w-9 h-9 text-[#FF914D]/40" />
                    <blockquote className="mt-4 text-lg md:text-xl text-white/90 leading-relaxed" style={DISPLAY}>
                        Depuis que nous utilisons Homly, le suivi de nos mandats est irréprochable.
                        Nos vendeurs sont rassurés et nos agents beaucoup plus efficaces.
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D]" />
                        <div>
                            <p className="text-sm font-semibold text-white">Directeur d'agence</p>
                            <p className="text-xs text-[#6E6A7A]">Client pilote Homly · témoignage anonymisé</p>
                        </div>
                    </figcaption>
                </motion.figure>
            </div>
        </section>
    );
};
